<template>
  <div class="cl-panel-stack">
    <article class="card">
      <header class="cl-card-head">
        <div>
          <p class="eyebrow">Abilities</p>
          <h3>{{ mode === 'damage' ? 'Group damage by ability' : 'Group healing by ability' }}</h3>
          <p class="muted-line">
            Group members only. Pet and guardian damage is attributed to the owner, so it appears under the owner's abilities.
          </p>
        </div>
        <div class="page-actions">
          <div class="cl-tab-switch" role="group" aria-label="Ability kind">
            <button
              class="btn-sm-outline"
              :class="{ 'is-active': mode === 'damage' }"
              type="button"
              @click="mode = 'damage'"
            >
              Damage
            </button>
            <button
              class="btn-sm-outline"
              :class="{ 'is-active': mode === 'healing' }"
              type="button"
              @click="mode = 'healing'"
            >
              Healing
            </button>
          </div>
        </div>
      </header>

      <div class="cl-table-scroll">
        <table class="cl-table">
          <thead>
            <tr>
              <th>Ability</th>
              <th class="is-numeric">{{ mode === 'damage' ? 'Damage' : 'Effective' }}</th>
              <th class="is-numeric">Share</th>
              <th class="is-numeric">Hits</th>
              <th class="is-numeric">Crit</th>
              <th class="is-numeric">Average</th>
              <th class="is-numeric">Max</th>
              <th class="is-numeric">{{ mode === 'damage' ? 'Overkill' : 'Overheal' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in activeRows" :key="row.key">
              <td>
                <span class="cl-ability-cell">
                  <strong>{{ row.name }}</strong>
                  <small v-if="row.school">{{ row.school }}</small>
                </span>
              </td>
              <td class="is-numeric">{{ formatNumber(row.amount) }}</td>
              <td class="is-numeric">{{ formatPercent(shareOf(row)) }}</td>
              <td class="is-numeric">{{ formatNumber(row.hits) }}</td>
              <td class="is-numeric">{{ row.hits ? formatPercent((row.crits || 0) / row.hits, 0) : '-' }}</td>
              <td class="is-numeric">{{ row.hits ? formatNumber(row.amount / row.hits) : '-' }}</td>
              <td class="is-numeric">{{ row.max ? formatNumber(row.max) : '-' }}</td>
              <td class="is-numeric">
                {{ mode === 'damage' ? formatNumber(row.overkill || 0) : formatNumber(row.overheal || 0) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="!activeRows.length" class="cl-empty-line">
        No {{ mode === 'damage' ? 'damage' : 'healing' }} abilities recorded in this segment.
      </p>
    </article>

    <div class="cl-two-column">
      <article class="card">
        <header class="cl-card-head">
          <div>
            <p class="eyebrow">Targets</p>
            <h3>Damage into enemies</h3>
            <p class="muted-line">Identical NPCs are grouped by creature id.</p>
          </div>
        </header>
        <CombatLogBars :rows="report.enemies" :limit="12" :formatter="formatNumber" />
      </article>

      <article class="card">
        <header class="cl-card-head">
          <div>
            <p class="eyebrow">Threats</p>
            <h3>Damage taken by source</h3>
            <p class="muted-line">What hit the group hardest in this segment.</p>
          </div>
        </header>
        <CombatLogBars
          :rows="report.damageTakenSources"
          :limit="12"
          color="var(--cl-taken)"
          :formatter="formatNumber"
        />
      </article>
    </div>
  </div>
</template>

<script>
import CombatLogBars from './CombatLogBars.vue'
import { formatNumber, formatPercent } from '../../services/combatLog/format'

export default {
  name: 'CombatLogAbilities',
  components: { CombatLogBars },
  props: {
    report: { type: Object, required: true },
  },
  data() {
    return { mode: 'damage' }
  },
  computed: {
    activeRows() {
      const rows = this.mode === 'damage' ? this.report.abilities.damage : this.report.abilities.healing
      return rows || []
    },
    total() {
      return this.activeRows.reduce((sum, row) => sum + (row.amount || 0), 0)
    },
  },
  methods: {
    formatNumber,
    formatPercent,
    shareOf(row) {
      if (!this.total) return 0
      return (row.amount || 0) / this.total
    },
  },
}
</script>
